import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getMySubscriptions, getFundCategories, subscribeToType, unsubscribeFromType, updatePledge, getTypeStats, getChurchProfile } from '../../services/api';
import { FiBell, FiCheckCircle, FiAlertTriangle, FiPlus, FiX } from 'react-icons/fi';
import Toast from '../../components/Toast';
import useAppRefresh from '../../hooks/useAppRefresh';

const freqColor = { weekly: 'pill-blue', monthly: 'pill-purple', yearly: 'pill-green' };

const MySubscriptions = () => {
  const { t } = useTranslation();
  const freqLabel = { weekly: t('mySubscriptions.weekly'), monthly: t('mySubscriptions.monthly'), yearly: t('mySubscriptions.yearly') };
  const [subscriptions, setSubscriptions] = useState([]);
  const [types, setTypes] = useState([]);
  const [stats, setStats] = useState({});
  const [showBrowse, setShowBrowse] = useState(false);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [subscribing, setSubscribing] = useState(null); // { typeId, amounts: {sym: value} }
  const [editPledge, setEditPledge] = useState(null);   // { typeId, amounts: {sym: value} }
  const [currencies, setCurrencies] = useState(['$', 'K']);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const loadSubs = () => getMySubscriptions().then(r => setSubscriptions(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  const loadTypes = () => getFundCategories().then(r => setTypes(Array.isArray(r.data) ? r.data.filter(c => c.frequency) : [])).catch(() => {});
  useAppRefresh(() => { loadSubs(); loadTypes(); });

  useEffect(() => {
    loadSubs();
    loadTypes();
    getChurchProfile().then(r => {
      const list = Array.isArray(r.data?.currencies) && r.data.currencies.length ? r.data.currencies : ['$', 'K'];
      setCurrencies(list);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (showBrowse) {
      types.forEach(type => {
        getTypeStats(type.id).then(r => setStats(prev => ({ ...prev, [type.id]: r.data }))).catch(() => {});
      });
    }
  }, [showBrowse, types]);

  const cleanPledges = (obj) => Object.fromEntries(
    Object.entries(obj || {}).filter(([, v]) => parseFloat(v) > 0).map(([k, v]) => [k, parseFloat(v)])
  );

  const handleSubscribe = async () => {
    if (!subscribing) return;
    setSaving(true);
    try {
      await subscribeToType({
        fund_category_id: subscribing.typeId,
        pledges: cleanPledges(subscribing.amounts),
      });
      showToast(t('mySubscriptions.subscribedSuccessfully'));
      setShowBrowse(false);
      setSubscribing(null);
      loadSubs();
    } catch (e) { showToast(e.response?.data?.error || t('myProfile.failed'), 'error'); }
    finally { setSaving(false); }
  };

  const handleUpdatePledge = async () => {
    if (!editPledge) return;
    setSaving(true);
    try {
      await updatePledge(editPledge.typeId, { pledges: cleanPledges(editPledge.amounts) });
      showToast(t('mySubscriptions.pledgeUpdated'));
      setEditPledge(null);
      loadSubs();
    } catch (e) { showToast(t('myProfile.failed'), 'error'); }
    finally { setSaving(false); }
  };

  const handleUnsubscribe = async (typeId, typeName) => {
    if (!window.confirm(t('mySubscriptions.unsubscribeConfirm', { name: typeName }))) return;
    await unsubscribeFromType(typeId);
    showToast(t('mySubscriptions.unsubscribed'), 'error');
    loadSubs();
  };

  const subscribedTypeIds = new Set(subscriptions.map(s => s.fund_category_id));
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('subscriptions.title')}</h1></div>
        <button className="btn btn-primary" onClick={() => setShowBrowse(true)}><FiPlus /> {t('subscriptions.browseTypes')}</button>
      </div>

      {subscriptions.length === 0 ? (
        <div className="card">
          <div className="empty-state" style={{ padding: '60px 24px' }}>
            <div className="empty-icon"><FiBell /></div>
            <h3>{t('subscriptions.noSubscriptions')}</h3>
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setShowBrowse(true)}>{t('subscriptions.browseTypes')}</button>
          </div>
        </div>
      ) : subscriptions.map(sub => {
        const period = sub.currentPeriod;
        const isOverdue = period && period.status === 'unpaid' && period.due_date < today;
        const isDue = period && period.status === 'unpaid' && period.due_date >= today;
        const isPaid = period?.status === 'paid';
        const freq = freqLabel[sub.frequency]?.toLowerCase();
        // Compute next due date from last paid period
        const lastPaidPeriod = (sub.history || []).find(h => h.status === 'paid');

        // Banner config per state
        let banner = null;
        if (isOverdue) {
          banner = {
            bg: '#fee2e2', color: '#b91c1c',
            msg: t('mySubscriptions.bannerOverdue', { freq, date: period.due_date })
          };
        } else if (isDue) {
          banner = {
            bg: '#fffbeb', color: '#92400e',
            msg: t('mySubscriptions.bannerDue', { freq, date: period.due_date })
          };
        } else if (isPaid) {
          // Find next unpaid period or estimate next cycle
          const nextUnpaid = (sub.history || []).find(h => h.status === 'unpaid');
          const nextDue = nextUnpaid?.due_date || '—';
          banner = {
            bg: '#f0fdf4', color: '#166534',
            msg: t('mySubscriptions.bannerPaid', { freq, date: nextDue })
          };
        } else {
          banner = {
            bg: '#f8fafc', color: '#6b7280',
            msg: t('mySubscriptions.bannerActive', { freq })
          };
        }

        return (
          <div key={sub.id} className="card" style={{ marginBottom: 16, border: isOverdue ? '1px solid #fca5a5' : isDue ? '1px solid #fde68a' : isPaid ? '1px solid #bbf7d0' : '1px solid #e5e7eb' }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: isOverdue ? '#fff1f2' : isDue ? '#fffbeb' : 'transparent' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <strong>{sub.type_name}</strong>
                <span className={`pill ${freqColor[sub.frequency]}`}>{freqLabel[sub.frequency]}</span>
                {(() => {
                  const pledgeEntries = Object.entries(sub.pledges || {}).filter(([, v]) => parseFloat(v) > 0);
                  if (pledgeEntries.length === 0) return null;
                  return (
                    <span style={{ fontSize: 12, color: '#6b7280' }}>
                      {t('mySubscriptions.myPledge')}:
                      {pledgeEntries.map(([sym, v], i) => (
                        <React.Fragment key={sym}>
                          {i > 0 && <span style={{ margin: '0 4px' }}>+</span>}
                          <strong style={{ color: '#2563eb', marginLeft: i === 0 ? 4 : 0 }}>{sym}{parseFloat(v).toFixed(2)}</strong>
                        </React.Fragment>
                      ))}
                      <span style={{ marginLeft: 4 }}>/ {freq}</span>
                    </span>
                  );
                })()}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {/* v1.6.4 — total owed across all unpaid periods, shown in
                    the header so the member sees the actual number at a
                    glance instead of just an 'Overdue' status pill. */}
                {(() => {
                  // v1.6.8 — sum remaining (pledge - amount_paid) across all
                  // unpaid periods so partial contributions reduce the figure
                  // instead of always showing pledge x count.
                  const unpaid = (sub.history || []).filter(h => h.status !== 'paid');
                  if (unpaid.length === 0) return null;
                  const totalsByCcy = {};
                  Object.entries(sub.pledges || {}).forEach(([sym, v]) => {
                    const pledge = parseFloat(v) || 0;
                    if (pledge <= 0) return;
                    const paidKey = sym === '$' ? 'amount_usd_paid' : sym === 'K' ? 'amount_kwacha_paid' : null;
                    const owed = unpaid.reduce((s, h) => s + Math.max(0, pledge - parseFloat(paidKey ? h[paidKey] : 0) || 0), 0);
                    if (owed > 0) totalsByCcy[sym] = owed;
                  });
                  const entries = Object.entries(totalsByCcy);
                  if (entries.length === 0) return null;
                  return (
                    <span style={{ fontSize: 13, fontWeight: 700, color: isOverdue ? '#b91c1c' : '#b45309' }}>
                      {t('mySubscriptions.totalOwed', { defaultValue: 'Owed:' })}{' '}
                      {entries.map(([sym, v], i) => (
                        <React.Fragment key={sym}>
                          {i > 0 && <span style={{ margin: '0 4px', fontWeight: 400 }}>+</span>}
                          {sym}{v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </React.Fragment>
                      ))}
                    </span>
                  );
                })()}
                {isOverdue && <span className="pill pill-red"><FiAlertTriangle style={{ fontSize: 10 }} /> {t('paymentStatus.overdue')}</span>}
                {isDue && <span className="pill pill-yellow"><FiBell style={{ fontSize: 10 }} /> {t('subscriptions.due')}</span>}
                {isPaid && <span className="pill pill-green"><FiCheckCircle style={{ fontSize: 10 }} /> {t('subscriptions.upToDate')}</span>}
                <span style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>
                  {t('subscriptions.contactTreasurer', { defaultValue: 'To change or cancel, see the treasurer' })}
                </span>
              </div>
            </div>

            {/* v1.8.1 — per-subscription banner keeps the alert text but drops
                the "Make Payment" button. The persistent Contribution Status
                Banner at the top of the portal already carries the primary
                "Pay now" CTA; a second button here just doubled up. */}
            {banner && (
              <div style={{ background: banner.bg, padding: '10px 20px', fontSize: 13, color: banner.color, fontWeight: 600 }}>
                <span>{banner.msg}</span>
              </div>
            )}

            <div className="card-body">
              <strong style={{ fontSize: 13 }}>{t('subscriptions.paymentHistory')}</strong>
              {(sub.history || []).length === 0 ? (
                <p style={{ fontSize: 13, color: '#9ca3af', marginTop: 8 }}>{t('mySubscriptions.noPaymentHistory')}</p>
              ) : (
                <table style={{ marginTop: 10 }}>
                  <thead><tr><th>{t('subscriptions.period')}</th><th>{t('subscriptions.dueDate')}</th><th>{t('common.status')}</th>{currencies.map(sym => <th key={sym}>{sym}</th>)}<th>{t('finance.crNumber')}</th></tr></thead>
                  <tbody>
                    {sub.history.map(h => {
                      // v1.6.8 — partial when unpaid but any amount already applied
                      const paidUsd = parseFloat(h.amount_usd_paid || 0);
                      const paidK   = parseFloat(h.amount_kwacha_paid || 0);
                      const hasPartial = h.status !== 'paid' && (paidUsd > 0 || paidK > 0);
                      const rowStatus = h.status === 'paid'
                        ? 'paid'
                        : hasPartial
                          ? 'partial'
                          : h.due_date < today ? 'overdue' : 'pending';
                      const statusPill = {
                        paid:    { cls: 'pill-green',  label: t('paymentStatus.paid') },
                        partial: { cls: 'pill-yellow', label: t('paymentStatus.partial', { defaultValue: 'Partial' }) },
                        overdue: { cls: 'pill-red',    label: t('paymentStatus.overdue') },
                        pending: { cls: 'pill-yellow', label: t('paymentStatus.pending') },
                      }[rowStatus];
                    return (
                      <tr key={h.id}>
                        <td>{h.period_label}</td>
                        <td>{h.due_date}</td>
                        <td>
                          <span className={`pill ${statusPill.cls}`}>{statusPill.label}</span>
                        </td>
                        {currencies.map((sym) => {
                          // v1.6.8 — Paid: pledge amount (green). Partial:
                          // 'paid / pledge' in amber. Fully unpaid: pledge
                          // in red (overdue) or amber (due soon).
                          const pledge = parseFloat((sub.pledges || {})[sym] || 0);
                          if (pledge <= 0) return <td key={sym}>—</td>;
                          const paidThis = sym === '$' ? paidUsd : sym === 'K' ? paidK : 0;
                          if (h.status === 'paid') {
                            return <td key={sym}>{sym}{pledge.toFixed(2)}</td>;
                          }
                          if (hasPartial && paidThis > 0) {
                            return (
                              <td key={sym} style={{ color: '#b45309', fontWeight: 600 }}>
                                {sym}{paidThis.toFixed(2)} / {sym}{pledge.toFixed(2)}
                              </td>
                            );
                          }
                          const isRowOverdue = h.due_date < today;
                          return (
                            <td key={sym} style={{ color: isRowOverdue ? '#b91c1c' : '#b45309', fontWeight: 600 }}>
                              {sym}{pledge.toFixed(2)}
                            </td>
                          );
                        })}
                        <td>{h.cr_number ? <span className="pill pill-gray" style={{ fontFamily: 'monospace', fontSize: 11 }}>{h.cr_number}</span> : '—'}</td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        );
      })}

      {editPledge && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <div><h3>{t('mySubscriptions.editPledgeTitle')}</h3><p>{t('mySubscriptions.editPledgeDesc')}</p></div>
              <button className="btn-icon" onClick={() => setEditPledge(null)}><FiX /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                {currencies.map(sym => (
                  <div className="form-group" key={sym}>
                    <label>Amount {sym}</label>
                    <input type="number" step="1" min="0" placeholder="0"
                      value={editPledge.amounts?.[sym] || ''}
                      onChange={e => setEditPledge(p => ({ ...p, amounts: { ...(p.amounts || {}), [sym]: e.target.value } }))} />
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditPledge(null)}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={handleUpdatePledge} disabled={saving}>{saving ? t('common.saving') : t('mySubscriptions.update')}</button>
            </div>
          </div>
        </div>
      )}

      {showBrowse && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <div><h3>{t('nav.contributionTypes')}</h3><p>{t('mySubscriptions.contributionTypesDesc')}</p></div>
              <button className="btn-icon" onClick={() => setShowBrowse(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              {types.length === 0 ? (
                <div className="empty-state"><p>{t('mySubscriptions.noRecurringTypes')}</p></div>
              ) : types.map(type => {
                const s = stats[type.id];
                const alreadySubbed = subscribedTypeIds.has(type.id);
                return (
                  <div key={type.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 12, background: alreadySubbed ? '#f0fdf4' : '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{type.name}</div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                          <span className={`pill ${freqColor[type.frequency]}`}>{freqLabel[type.frequency]}</span>
                          <span style={{ fontSize: 12, color: '#6b7280' }}>{t('mySubscriptions.dueDay', { day: type.due_day || 1 })}</span>
                        </div>
                        {type.description && <p style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>{type.description}</p>}
                        {s && s.payment_count > 0 && (
                          <div style={{ marginTop: 10, background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
                            <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>{t('mySubscriptions.whatMembersContribute')}</div>
                            {s.avg_usd > 0 && <div style={{ color: '#6b7280' }}>$ — Min: <strong>${parseFloat(s.min_usd).toFixed(2)}</strong> · Avg: <strong>${parseFloat(s.avg_usd).toFixed(2)}</strong> · Max: <strong>${parseFloat(s.max_usd).toFixed(2)}</strong></div>}
                            {s.avg_k > 0 && <div style={{ color: '#6b7280', marginTop: 2 }}>K — Min: <strong>K{parseFloat(s.min_k).toFixed(2)}</strong> · Avg: <strong>K{parseFloat(s.avg_k).toFixed(2)}</strong> · Max: <strong>K{parseFloat(s.max_k).toFixed(2)}</strong></div>}
                          </div>
                        )}
                        {subscribing?.typeId === type.id && (
                          <div style={{ marginTop: 12, background: '#eef2ff', border: '1px solid #a5b4fc', borderRadius: 8, padding: '12px 14px' }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#3730a3', marginBottom: 8 }}>{t('mySubscriptions.howMuchPlan', { freq: freqLabel[type.frequency]?.toLowerCase() })}</div>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                              {currencies.map(sym => (
                                <div key={sym}>
                                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>{sym}</div>
                                  <input type="number" step="1" min="0" placeholder="0"
                                    value={subscribing.amounts?.[sym] || ''}
                                    onChange={e => setSubscribing(p => ({ ...p, amounts: { ...(p.amounts || {}), [sym]: e.target.value } }))}
                                    style={{ width: 110 }} />
                                </div>
                              ))}
                              <button className="btn btn-primary btn-sm" onClick={handleSubscribe}
                                disabled={saving || !Object.values(subscribing?.amounts || {}).some(v => parseFloat(v) > 0)}
                                style={{ marginTop: 18 }}>
                                {saving ? t('common.saving') : t('mySubscriptions.confirm')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <div style={{ flexShrink: 0, marginLeft: 16 }}>
                        {alreadySubbed
                          ? <span className="pill pill-green"><FiCheckCircle /> {t('subscriptions.subscribed')}</span>
                          : subscribing?.typeId === type.id
                            ? <button className="btn btn-sm btn-secondary" onClick={() => setSubscribing(null)}>{t('common.cancel')}</button>
                            : <button className="btn btn-primary btn-sm" onClick={() => setSubscribing({ typeId: type.id, amounts: {} })}>{t('subscriptions.subscribe')}</button>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowBrowse(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}
      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default MySubscriptions;
